import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function ApiReferencePage() {
  return (
    <DocsLayout
      title="API reference"
      updated="August 26, 2026"
      summary="Authenticate and automate common Quality Engineering workflows. Many routes are mounted under both /api and /api/v1. Prefer the OpenAPI surface in non-hardened environments for the full catalogue."
    >
      <h2>Authentication</h2>
      <p>
        Mint an API key from <strong>Settings → API keys</strong>. Send it as <code>Authorization: Bearer rny_…</code>{' '}
        on every request. Keys are scoped to a single organization and can be revoked instantly from the same screen.
      </p>

      <h2>Base URLs</h2>
      <table>
        <thead>
          <tr>
            <th>Environment</th>
            <th>Base URL</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Production</td>
            <td>
              <code>https://api.rynora.app</code> (routes under <code>/api/…</code> and often <code>/api/v1/…</code>)
            </td>
          </tr>
          <tr>
            <td>UAT</td>
            <td>
              <code>https://uat.rynora.app</code>
            </td>
          </tr>
          <tr>
            <td>QA</td>
            <td>
              <code>https://qa.rynora.app</code>
            </td>
          </tr>
          <tr>
            <td>Self-host / local</td>
            <td>
              <code>https://&lt;your-host&gt;</code> or <code>http://localhost:3001</code>
            </td>
          </tr>
        </tbody>
      </table>

      <h2>Common endpoints</h2>

      <h3>List flows</h3>
      <pre className="docs-codeblock">{`GET /api/projects/:projectId/flows
Authorization: Bearer rny_xxx`}</pre>

      <h3>Trigger a run</h3>
      <pre className="docs-codeblock">{`POST /api/runs
Authorization: Bearer rny_xxx
Content-Type: application/json

{
  "flowId": "…",
  "browser": "chromium"
}`}</pre>

      <h3>Release Confidence (advisory)</h3>
      <pre className="docs-codeblock">{`GET /api/projects/:projectId/release-confidence
Authorization: Bearer rny_xxx`}</pre>

      <h3>Evidence export</h3>
      <pre className="docs-codeblock">{`GET /api/projects/:projectId/evidence/export?format=json
Authorization: Bearer rny_xxx`}</pre>

      <h3>Playwright project egress (published flows)</h3>
      <pre className="docs-codeblock">{`GET /api/projects/:projectId/flows/:flowId/export/playwright/preview
GET /api/projects/:projectId/flows/:flowId/export/playwright
Authorization: Bearer rny_xxx`}</pre>

      <h3>Webhook callbacks</h3>
      <p>
        Configure a webhook URL under <strong>Settings → Notifications</strong>. Rynora POSTs run completion events. In
        hardened environments (UAT + Production) the URL must be HTTPS.
      </p>

      <h2>Quality Intelligence notes</h2>
      <ul>
        <li>Change Impact / TIA / Failure Intelligence routes are project-scoped and authenticated.</li>
        <li>Release Confidence and gates are advisory unless your own CI policy treats them as blocking.</li>
        <li>ADO CI emit is buyer-gated; do not assume Azure Pipelines status-check parity.</li>
      </ul>

      <h2>Versioning &amp; deprecation</h2>
      <p>
        Prefer documented dual mounts (<code>/api</code> and <code>/api/v1</code>). Breaking changes are announced with
        notice via changelog where applicable.
      </p>

      <h2>SDKs &amp; OpenAPI</h2>
      <p>
        First-party SDK packages exist in the repository for typed clients — see in-repo SDK docs. The OpenAPI UI is
        available at <code>/api/docs</code> in non-hardened environments; hardened deployments may disable the public
        Swagger UI. For day-to-day examples, start with the <Link to="/docs/getting-started">Quickstart</Link> and the
        in-app Help API section.
      </p>
    </DocsLayout>
  );
}
